import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { MessageSquare, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EstadoBadge } from '@/components/domain/EstadoBadge';
import { EstadoSelector } from '@/components/domain/EstadoSelector';
import { TransporteBadge } from '@/components/domain/TransporteBadge';
import { api, ApiError } from '@/lib/api';
import { fmtShortDate, fmtRelative } from '@/lib/format';
import type { Item, ItemNote, StatusChange } from '@/lib/types';
import { useAuth } from '@/lib/auth';

interface Props {
  itemId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ItemWithBooking = Item & {
  booking: {
    id: number;
    bookingIdExterno: string;
    clienteNombre: string;
    transporte: string;
    modo: string;
    direccion: string;
    comercialNombre: string | null;
    etd: string | null;
    eta: string | null;
    agenteId: number;
  };
  _count: { children: number };
};

export function ItemDetailDialog({ itemId, open, onOpenChange }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const itemQ = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => api<ItemWithBooking>(`/items/${itemId}`),
    enabled: open,
  });

  const historyQ = useQuery({
    queryKey: ['item-history', itemId],
    queryFn: () => api<{ changes: StatusChange[] }>(`/items/${itemId}/history`),
    enabled: open,
  });

  const notesQ = useQuery({
    queryKey: ['item-notes', itemId],
    queryFn: () => api<{ notes: ItemNote[] }>(`/items/${itemId}/notes`),
    enabled: open,
  });

  const [draft, setDraft] = useState('');
  const addNoteMut = useMutation({
    mutationFn: (texto: string) =>
      api<ItemNote>(`/items/${itemId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ texto }),
      }),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['item-notes', itemId] });
      // Refrescar también el booking-level notes (para el botón "Notas del booking")
      if (itemQ.data?.booking.id) {
        qc.invalidateQueries({
          queryKey: ['booking-notes', itemQ.data.booking.id],
        });
      }
      // Refresh listings que muestran contador de notas
      qc.invalidateQueries({ queryKey: ['booking-items'] });
      toast.success('Nota agregada');
    },
    onError: (err: any) =>
      toast.error(err instanceof ApiError ? err.message : 'Error al guardar nota'),
  });

  const item = itemQ.data;
  const isLeaf = item ? item._count.children === 0 : true;
  // Editable si admin, gerente (el item solo se abre desde un booking ya
  // visible en su scope), o el agente dueño. El backend valida en el PATCH.
  const canEdit =
    item != null &&
    (user?.rol === 'admin' ||
      user?.rol === 'director' ||
      user?.rol === 'gerente' ||
      user?.id === item.booking.agenteId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent side="right" className="p-6 overflow-y-auto">
        <DialogTitle className="pr-8">
          {item ? (
            <>
              <span className="tabular text-sm text-ink-400 mr-2">{item.hierarchyId}</span>
              {item.nombre}
            </>
          ) : (
            'Cargando…'
          )}
        </DialogTitle>

        {item && (
          <div className="mt-4 space-y-5">
            {/* Booking */}
            <div className="p-3 rounded-md bg-surface-2 border border-ink-200 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium tabular">{item.booking.bookingIdExterno}</span>
                <span className="text-ink-400">·</span>
                <span>{item.booking.clienteNombre}</span>
                <TransporteBadge
                  transporte={item.booking.transporte}
                  modo={item.booking.modo}
                  direccion={item.booking.direccion}
                />
              </div>
              <div className="text-xs text-ink-400">
                {item.booking.comercialNombre && (
                  <>
                    Comercial: <span className="text-ink-900">{item.booking.comercialNombre}</span>{' '}
                    ·{' '}
                  </>
                )}
                ETD {fmtShortDate(item.booking.etd)} · ETA {fmtShortDate(item.booking.eta)}
              </div>
            </div>

            {/* Estado */}
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase">Estado</label>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                <EstadoSelector
                  itemId={item.id}
                  estado={item.estado}
                  isLeaf={isLeaf}
                  canEdit={canEdit}
                />
                {!isLeaf && canEdit && (
                  <span className="text-xs text-amber-700">
                    Marcar/desmarcar lo aplica a todas sus subtareas
                  </span>
                )}
              </div>
              {item.predecesoras && item.predecesoras !== '0' && (
                <p className="mt-2 text-xs text-ink-400">
                  Predecesora: <span className="tabular text-ink-700">{item.predecesoras}</span>
                </p>
              )}
            </div>

            {/* Fase + fecha límite */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-ink-400 uppercase">Fase</label>
                <div className="mt-1 text-sm">{item.fase ?? '—'}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-400 uppercase">Vence</label>
                <div className="mt-1 text-sm tabular">{fmtShortDate(item.fechaLimite)}</div>
              </div>
            </div>

            {/* SLA */}
            {item.notasSla && (
              <div>
                <label className="text-xs font-medium text-ink-400 uppercase">SLA / Notas</label>
                <pre className="mt-1 text-xs text-ink-700 whitespace-pre-wrap font-sans bg-surface-2 border border-ink-200 rounded p-3">
                  {item.notasSla}
                </pre>
              </div>
            )}

            {/* Notas del operativo (append-only, con timestamp + autor) */}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-ink-400 uppercase flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  Notas del operativo ({notesQ.data?.notes?.length ?? 0})
                </label>
              </div>
              <p className="text-[11px] text-ink-400 mt-0.5">
                Append-only. Cada nota queda guardada con autor y fecha como evidencia.
                No se puede editar ni borrar.
              </p>

              {/* Form para agregar nota */}
              {canEdit && (
                <div className="mt-2">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Ej: bloqueada porque el cliente no envía la factura comercial…"
                    rows={2}
                    maxLength={2000}
                    className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-ink-400">
                      {draft.length}/2000
                    </span>
                    <Button
                      size="sm"
                      onClick={() => addNoteMut.mutate(draft.trim())}
                      disabled={!draft.trim() || addNoteMut.isPending}
                    >
                      <Send className="h-3 w-3" />
                      {addNoteMut.isPending ? 'Guardando…' : 'Agregar nota'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Timeline de notas (más reciente arriba) */}
              <div className="mt-3 space-y-2">
                {notesQ.isLoading && (
                  <p className="text-xs text-ink-400">Cargando notas…</p>
                )}
                {!notesQ.isLoading && (notesQ.data?.notes?.length ?? 0) === 0 && (
                  <p className="text-sm text-ink-400 italic">
                    Sin notas todavía.
                  </p>
                )}
                {notesQ.data?.notes?.map((n) => (
                  <div
                    key={n.id}
                    className="border-l-2 border-amber-400 pl-3 py-1.5 text-sm bg-amber-50/40 rounded-r"
                  >
                    <p className="whitespace-pre-wrap text-ink-900">{n.texto}</p>
                    <div className="text-[11px] text-ink-400 mt-1">
                      por <span className="font-medium text-ink-700">{n.user.nombre}</span>{' '}
                      · {fmtRelative(n.createdAt)}{' '}
                      <span className="text-ink-300">
                        ({new Date(n.createdAt).toLocaleString('es-MX', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Historial */}
            <div>
              <label className="text-xs font-medium text-ink-400 uppercase">Historial</label>
              <div className="mt-2 space-y-2">
                {historyQ.data?.changes?.length === 0 && (
                  <p className="text-sm text-ink-400">Sin cambios todavía.</p>
                )}
                {historyQ.data?.changes?.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-start gap-3 text-sm border-l-2 border-ink-200 pl-3 py-1"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <EstadoBadge estado={c.estadoPrevio} showLabel={false} />
                        <span className="text-ink-400">→</span>
                        <EstadoBadge estado={c.estadoNuevo} showLabel={false} />
                      </div>
                      <div className="text-xs text-ink-400 mt-0.5">
                        por {c.user.nombre} · {fmtRelative(c.changedAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
